import React, { useCallback, useRef, useState, useEffect } from "react";
import { PhotoLocation } from "../types/protocols";
import { useFirebaseImage, ImageSize, getImagePathWithSize } from "../Helpers/ImageLoader";
import { Fancybox } from "@fancyapps/ui";
import Marker from "./Marker";
import "./PhotoThumbnailWithMarker.css";

interface PhotoThumbnailWithMarkerProps {
    photoLocation: PhotoLocation;
    size?: number;
    onClick?: () => void;
    disableClick?: boolean;
}

const PhotoThumbnailWithMarker: React.FC<PhotoThumbnailWithMarkerProps> = ({
    photoLocation,
    size = 112,
    onClick,
    disableClick = false
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });
    
    // Use scaled small resolution image for thumbnail (preserves aspect ratio)
    // Fall back to full-size if scaled version doesn't exist
    const scaledPath = getImagePathWithSize(photoLocation?.storagePath, ImageSize.SCALED_SMALL);
    const fullPath = photoLocation?.storagePath;
    const { src: scaledSrc, hasError: scaledHasError } = useFirebaseImage(scaledPath);
    const { src: fullSrc, isLoading, hasError: fullHasError } = useFirebaseImage(fullPath);
    
    // Use scaled version if available, otherwise fall back to full-size
    const src = scaledHasError ? fullSrc : scaledSrc;
    const hasError = scaledHasError && fullHasError;
    
    const handleClick = useCallback((e: React.MouseEvent) => {
        if (disableClick) {
            return;
        }
        
        e.preventDefault();
        e.stopPropagation();
        
        if (onClick) {
            onClick();
            return;
        }
        
        // Open in FancyBox if we have a source (prefer full-size for better quality)
        if (fullSrc || src) {
            Fancybox.show(
                [{
                    src: fullSrc || src,
                    caption: 'Position i foto',
                }],
                {
                    animated: true,
                    showClass: "fancybox-fadeIn",
                }
            );
        }
    }, [onClick, fullSrc, src, disableClick]);

    // Calculate container dimensions based on image aspect ratio
    const containerStyle = React.useMemo(() => {
        if (!photoLocation) return { width: `${size}px`, height: `${size}px` };
        
        const { width: photoWidth, height: photoHeight } = photoLocation;
        const aspectRatio = photoWidth / photoHeight;
        
        let width: number;
        let height: number;
        
        if (aspectRatio > 1) {
            // Landscape: max out width
            width = size;
            height = size / aspectRatio;
        } else {
            // Portrait or square: max out height
            height = size;
            width = size * aspectRatio;
        }
        
        return { width: `${width}px`, height: `${height}px` };
    }, [photoLocation, size]);

    const calculateMarkerPosition = useCallback(() => {
        if (!photoLocation || !containerDimensions.width) return null;

        const { x, y, width: photoWidth } = photoLocation;
        const { width: containerWidth } = containerDimensions;

        // Simple scale calculation since container matches image aspect ratio (no cropping)
        const scale = containerWidth / photoWidth;

        // Apply scale to marker position
        const scaledX = x * scale;
        const scaledY = y * scale;

        const markerSize = 10; // Smaller marker for thumbnail
        return {
            left: `${scaledX}px`,
            top: `${scaledY}px`,
            width: `${markerSize}px`,
            height: `${markerSize}px`,
        };
    }, [photoLocation, containerDimensions]);

    // Update container dimensions
    useEffect(() => {
        const updateDimensions = () => {
            if (containerRef.current) {
                setContainerDimensions({
                    width: containerRef.current.offsetWidth,
                    height: containerRef.current.offsetHeight
                });
            }
        };

        const resizeObserver = new ResizeObserver(updateDimensions);

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        updateDimensions();

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    if (isLoading) {
        return (
            <div
                style={{
                    ...containerStyle,
                    backgroundColor: '#f0f0f0',
                    borderRadius: '5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px',
                    color: '#666'
                }}
            >
                Laddar...
            </div>
        );
    }

    if (hasError || !src) {
        return (
            <div
                style={{
                    ...containerStyle,
                    backgroundColor: '#f0f0f0',
                    borderRadius: '5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px',
                    color: '#666'
                }}
            >
                Foto saknas
            </div>
        );
    }

    const markerPosition = calculateMarkerPosition();

    return (
        <div
            ref={containerRef}
            className={`photo-thumbnail-with-marker ${disableClick ? 'disabled' : ''}`}
            onClick={disableClick ? undefined : handleClick}
            style={{
                position: 'relative',
                ...containerStyle,
                overflow: 'visible',
                cursor: disableClick ? 'default' : 'pointer',
            }}
        >
            <div style={{
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                borderRadius: '5px',
                border: '1px solid #ddd',
            }}>
                <img
                    src={src}
                    alt="Position i foto"
                    onLoad={() => {
                        if (containerRef.current) {
                            setContainerDimensions({
                                width: containerRef.current.offsetWidth,
                                height: containerRef.current.offsetHeight
                            });
                        }
                    }}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block'
                    }}
                />
            </div>

            {markerPosition && (
                <Marker position={markerPosition} />
            )}
        </div>
    );
};

export default PhotoThumbnailWithMarker;

